import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, PenTool, FileCheck, Loader2 } from "lucide-react";
import Handlebars from "handlebars";
import { sanitizeDocumentContent } from "@/lib/sanitize";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { NativePdfPreview } from "@/components/documents/NativePdfPreview";
import { getDocumentSignedUrl, getSignedUrl } from "@/lib/storageUtils";
import {
  downloadDocument,
  getDocumentSignedUrl as getDocSignedUrl,
  getDocumentStoragePath,
  hasDownloadablePath,
} from "@/lib/documentDownload";
import { humanDocumentStatus } from "@/lib/humanDocumentStatus";
interface DocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  document: any;
  onRequestSignatures?: (document: any) => void;
  onFinalized?: () => void;
  onSignAndFinalize?: () => void;
  hideDownloadLinks?: boolean;
  hideFinalizeButton?: boolean;
}

export function DocumentPreviewModal({ open, onClose, document, onRequestSignatures, onFinalized, onSignAndFinalize, hideDownloadLinks, hideFinalizeButton }: DocumentPreviewModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPollingSignedDoc, setIsPollingSignedDoc] = useState(false);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  // Load document content for HTML preview
  useEffect(() => {
    if (open && document) {
      loadDocumentContent();
      loadPdfPreviewUrl();
    }
  }, [open, document?.id]);

  const loadPdfPreviewUrl = async () => {
    const path = getDocumentStoragePath(document || {});
    if (!path) {
      setPreviewUrl(null);
      return;
    }
    const url = await getSignedUrl('documents', path);
    setPreviewUrl(url);
  };

  const loadDocumentContent = async () => {
    if (!document) return;
    
    try {
      setLoading(true);
      
      // If custom content exists, use it directly
      if (document.custom_content) {
        setContent(document.custom_content);
        setLoading(false);
        return;
      }
      
      // Otherwise, fetch and rebuild from template + merge_data
      const { data: docData, error } = await supabase
        .from("generated_documents")
        .select(`
          *,
          document_sections(*),
          template_lib:template_library_id(template_content)
        `)
        .eq("id", document.id)
        .single();
      
      if (error) throw error;
      
      // Rebuild HTML from template if available
      if (docData.template_lib?.template_content && docData.merge_data) {
        try {
          // Convert string clauses to arrays for Handlebars {{#each}}
          const preparedMergeData = { ...(docData.merge_data as Record<string, any> || {}) };
          
          if (typeof preparedMergeData.whereas_clauses === 'string') {
            preparedMergeData.whereas_clauses = preparedMergeData.whereas_clauses
              .split('\n')
              .map((line: string) => line.trim())
              .filter((line: string) => line.length > 0);
          }
          
          if (typeof preparedMergeData.resolved_clauses === 'string') {
            preparedMergeData.resolved_clauses = preparedMergeData.resolved_clauses
              .split('\n')
              .map((line: string) => line.trim())
              .filter((line: string) => line.length > 0);
          }
          
          const template = Handlebars.compile(docData.template_lib.template_content);
          const html = template(preparedMergeData);
          setContent(html);
        } catch (templateError) {
          console.error('Error compiling template:', templateError);
          // Fall through to sections fallback
        }
      } else if (docData.document_sections && docData.document_sections.length > 0) {
        // Fallback: build from sections
        const sectionsHtml = docData.document_sections
          .sort((a: any, b: any) => (a.section_number || 0) - (b.section_number || 0))
          .map((section: any) => `
            <div class="section">
              <h3>${section.section_title || ''}</h3>
              ${section.section_content || ''}
            </div>
          `)
          .join('\n');
        setContent(sectionsHtml);
      } else {
        setContent('No content available');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading document content:', error);
      setContent('<div class="text-center p-8">Error loading document content</div>');
      setLoading(false);
    }
  };

  // Poll for signed document if all signatures collected but signed_file_url not ready
  useEffect(() => {
    if (!document?.all_signatures_collected) return;
    
    const signatureOverlayStatus = (document as any).signature_overlay_status;
    const signedFileUrl = (document as any).signed_file_url;
    
    // Stop polling if completed or failed
    if (signedFileUrl) {
      setIsPollingSignedDoc(false);
      toast({ 
        title: 'Signed Document Ready!', 
        description: 'Your signed document is now available for download.',
        duration: 5000
      });
      return;
    }
    
    if (signatureOverlayStatus === 'failed') {
      setIsPollingSignedDoc(false);
      toast({ 
        title: 'Signature step did not finish', 
        description: 'The signed copy could not be built. You can try again below.',
        variant: 'destructive',
        duration: 8000
      });
      return;
    }

    setIsPollingSignedDoc(true);
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('generated_documents')
        .select('signed_file_url, signature_overlay_status')
        .eq('id', document.id)
        .single();

      if (data?.signed_file_url) {
        setIsPollingSignedDoc(false);
        window.location.reload(); // Refresh to show signed document
      } else if (data?.signature_overlay_status === 'failed') {
        setIsPollingSignedDoc(false);
        window.location.reload(); // Refresh to show error state
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [document?.all_signatures_collected, document?.id, toast]);

  if (!document) return null;

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'bg-gray-500',
      pending_review: 'bg-yellow-500',
      final: 'bg-green-500',
      signed: 'bg-blue-500',
      archived: 'bg-gray-400',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500';
  };

  const getStatusLabel = (status: string) => humanDocumentStatus(status);


  const handleFinalize = async () => {
    if (!(document.status === 'draft' || document.status === 'pending_review')) return;
    if (!confirm('Close this document as final? A new PDF will be built from what is here now.')) return;
    
    try {
      setSaving(true);

      // Use shared HTML generation to preserve template + signature block
      toast({ title: 'Generating PDF...', description: 'Building from current document content' });
      
      const { generateDocumentPdf } = await import('@/lib/pdfGeneration');
      
      const pdfResult = await generateDocumentPdf(document.id, document.document_name);
      const fileUrl = pdfResult.file_url;
      const filePath = pdfResult.file_path;
      const fileSize = pdfResult.file_size;

      const { error } = await supabase
        .from('generated_documents')
        .update({
          status: 'final',
          finalized_at: new Date().toISOString(),
          finalized_by: user?.id,
          file_url: fileUrl,
          file_path: filePath,
          file_size: fileSize,
        })
        .eq('id', document.id);

      if (error) throw error;

      // Update phase document requirements
      try {
        await supabase.functions.invoke('update-phase-document-status', {
          body: { documentId: document.id, status: 'finalized' }
        });
      } catch (phaseError) {
        console.error('Error updating phase requirements:', phaseError);
        // Don't fail the finalization if phase update fails
      }

      if (fileSize < 2000) {
        toast({ 
          title: 'Warning: Small PDF', 
          description: `Generated PDF is only ${fileSize} bytes. Please try regenerating the document if content is missing.`,
          variant: 'destructive'
        });
      } else {
        toast({ title: 'Document Finalized', description: 'PDF generated successfully' });
      }
      
      onFinalized?.();
      onClose();
    } catch (e: any) {
      console.error('Error finalizing document from preview:', e);
      toast({ title: 'Error', description: e.message || 'Failed to finalize document', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }; 

  const handleRetrySignatureOverlay = async () => {
    try {
      setRebuilding(true);
      toast({ title: 'Retrying Signature Overlay...', description: 'Applying signatures to PDF again' });

      // Reset status to pending
      await supabase
        .from('generated_documents')
        .update({ 
          signature_overlay_status: 'pending',
          signed_file_url: null 
        })
        .eq('id', document.id);

      // Trigger overlay again
      const { data: signatureRequest } = await supabase
        .from('signature_requests')
        .select('id')
        .eq('document_id', document.id)
        .single();

      if (signatureRequest) {
        const { error } = await supabase.functions.invoke('apply-signatures-to-pdf', {
          body: {
            documentId: document.id,
            signatureRequestId: signatureRequest.id,
          },
        });

        if (error) throw error;

        toast({ title: 'Retry Started', description: 'Signature overlay is being regenerated...' });
        setIsPollingSignedDoc(true); // Start polling again
      }
    } catch (e: any) {
      console.error('Error retrying signature overlay:', e);
      toast({ title: 'Retry Failed', description: e.message || 'Failed to retry signature overlay', variant: 'destructive' });
    } finally {
      setRebuilding(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-xl">{document.document_name}</DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge className={getStatusColor(document.status)}>
                  {getStatusLabel(document.status)}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Version {document.version}
                </span>
                <span className="text-sm text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground">
                  {document.generated_at 
                    ? format(new Date(document.generated_at), 'MMM d, yyyy')
                    : 'Not available'}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mr-6 sm:mr-10">
          {document.all_signatures_collected && hasDownloadablePath({
            file_path: (document as any).file_path,
            file_url: (document as any).file_url,
            signed_file_path: (document as any).signed_file_path,
            signed_file_url: (document as any).signed_file_url,
          }) && (
              <Button
                size="sm"
                onClick={async () => {
                  try {
                    await downloadDocument(document as any, { signed: true });
                    toast({ title: 'Download Started', description: 'Signed document is downloading...' });
                  } catch (err: any) {
                    console.error('Download error:', err);
                    toast({ title: 'Download Failed', description: err?.message || 'Could not download signed document', variant: 'destructive' });
                  }
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Signed Document
              </Button>
          )}

              {document.all_signatures_collected && (document as any).signature_overlay_status === 'processing' && !(document as any).signed_file_url && (
                <div className="flex items-center gap-2 text-blue-600 px-3 py-2 bg-blue-50 rounded-md">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm font-medium">Generating Signed Document...</span>
                </div>
              )}

              {document.all_signatures_collected && (document as any).signature_overlay_status === 'failed' && !(document as any).signed_file_url && (
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="text-sm">Overlay Failed</Badge>
                  <Button 
                    onClick={handleRetrySignatureOverlay} 
                    variant="outline" 
                    size="sm"
                    disabled={rebuilding}
                  >
                    {rebuilding ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileCheck className="h-4 w-4 mr-2" />
                    )}
                    Retry Signature Overlay
                  </Button>
                </div>
              )}

              {!document.all_signatures_collected && (
                <>
                  {(document.status === 'draft' || document.status === 'pending_review') && (
                    <>
                      {!hideFinalizeButton && onSignAndFinalize && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={onSignAndFinalize}
                          disabled={saving}
                        >
                          <PenTool className="h-4 w-4 mr-2" />
                          Sign & Finalize
                        </Button>
                      )}
                    </>
                  )}
                  {document.status === 'final' && onRequestSignatures && (
                    <Button
                      size="sm"
                      onClick={() => {
                        onRequestSignatures(document);
                        onClose();
                      }}
                    >
                      <PenTool className="h-4 w-4 mr-2" />
                      Request Signatures
                    </Button>
                  )}
                </>
              )}
              {!hideDownloadLinks && hasDownloadablePath(document) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await downloadDocument(document);
                      toast({ title: 'Download Started' });
                    } catch (err: any) {
                      console.error('Download error:', err);
                      toast({ title: 'Download Failed', description: err?.message || 'Could not download document', variant: 'destructive' });
                    }
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="mt-4 border rounded-lg p-6 max-h-[60vh] overflow-y-auto bg-white">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">Loading preview...</span>
            </div>
          ) : content ? (
          <div 
            className="ecclesiastical-document max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(content) }}
          />
          ) : previewUrl ? (
            <NativePdfPreview url={previewUrl} maxWidth={900} heightClassName="h-[60vh]" />
          ) : (
            // Show "not available" message
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">Document Preview Not Available</h3>
              <p className="text-sm text-muted-foreground">
                This document hasn't been generated yet or has no content to preview.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
